import { useEffect, useState } from "react";
import { useTenantAuth } from "../../context/auth";

export function TgSettingsPage() {
  const { bootstrap, updateTenantMessagingStrategy } = useTenantAuth();
  const [marketProfile, setMarketProfile] = useState<"default" | "telegram_first" | "whatsapp_first">("default");
  const [primaryMessenger, setPrimaryMessenger] = useState<"" | "telegram" | "whatsapp" | "sms" | "email">("");
  const [messengerFirstMode, setMessengerFirstMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setMarketProfile((bootstrap?.tenant.market_profile as "default" | "telegram_first" | "whatsapp_first") ?? "default");
    setPrimaryMessenger((bootstrap?.tenant.primary_messenger as "" | "telegram" | "whatsapp" | "sms" | "email") ?? "");
    setMessengerFirstMode(Boolean(bootstrap?.tenant.messenger_first_mode));
  }, [bootstrap?.tenant.market_profile, bootstrap?.tenant.primary_messenger, bootstrap?.tenant.messenger_first_mode]);

  async function save() {
    setSaving(true);
    setNotice("");
    setError("");
    try {
      await updateTenantMessagingStrategy({
        market_profile: marketProfile,
        primary_messenger: primaryMessenger || null,
        messenger_first_mode: messengerFirstMode,
        apply_profile_defaults: false,
      });
      setNotice("Settings saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 p-4">
      <h1 className="text-lg font-bold text-gray-900">Settings</h1>

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tenant profile</p>
        <div className="mt-2 grid gap-1 text-sm text-gray-700">
          <p>Name: {bootstrap?.tenant.name || "-"}</p>
          <p>Slug: {bootstrap?.tenant.slug || "-"}</p>
          <p>Status: {bootstrap?.tenant.status || "-"}</p>
          <p>Locale: {bootstrap?.tenant.default_locale || "-"}</p>
          <p>Plan: {bootstrap?.tenant.plan_code || "-"}</p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Messaging strategy</p>
        <label className="block text-sm">
          <span className="mb-1 block text-gray-600">Market profile</span>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
            value={marketProfile}
            onChange={(e) => setMarketProfile(e.target.value as "default" | "telegram_first" | "whatsapp_first")}
          >
            <option value="default">default</option>
            <option value="telegram_first">telegram_first</option>
            <option value="whatsapp_first">whatsapp_first</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-gray-600">Primary messenger</span>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
            value={primaryMessenger}
            onChange={(e) => setPrimaryMessenger(e.target.value as "" | "telegram" | "whatsapp" | "sms" | "email")}
          >
            <option value="">(none)</option>
            <option value="telegram">telegram</option>
            <option value="whatsapp">whatsapp</option>
            <option value="sms">sms</option>
            <option value="email">email</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={messengerFirstMode} onChange={(e) => setMessengerFirstMode(e.target.checked)} />
          Messenger-first mode
        </label>
        <button
          className="w-full rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={saving}
          onClick={save}
        >
          {saving ? "Saving..." : "Save settings"}
        </button>
        {notice && <div className="rounded-lg bg-emerald-50 p-2 text-sm text-emerald-700">{notice}</div>}
        {error && <div className="rounded-lg bg-rose-50 p-2 text-sm text-rose-700">{error}</div>}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Channel capabilities</p>
        <div className="mt-2 space-y-2">
          {Object.entries(bootstrap?.channel_capabilities ?? {}).map(([key, caps]) => (
            <div key={key} className="rounded-lg border border-gray-100 bg-gray-50 p-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <strong className="text-gray-800">{caps.label}</strong>
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-gray-500">{caps.rollout_mode}</span>
              </div>
              <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-1 text-gray-600">
                <span>Mgr app: {caps.supports_manager_embedded_app ? "yes" : "no"}</span>
                <span>Client app: {caps.supports_client_embedded_app ? "yes" : "no"}</span>
                <span>Deep links: {caps.supports_deep_links ? "yes" : "no"}</span>
                <span>Linking: {caps.supports_identity_linking ? "yes" : "no"}</span>
              </div>
            </div>
          ))}
          {Object.keys(bootstrap?.channel_capabilities ?? {}).length === 0 && (
            <p className="text-sm text-gray-500">No channel capability metadata.</p>
          )}
        </div>
      </div>
    </div>
  );
}
